/**
 * Value Object: Distance
 * Represents a distance with validation and unit conversion
 */
export class Distance {
  private readonly _meters: number;

  constructor(meters: number) {
    if (meters < 0) {
      throw new Error('Distance cannot be negative');
    }
    this._meters = meters;
  }

  static fromMeters(meters: number): Distance {
    return new Distance(meters);
  }

  static fromKilometers(km: number): Distance {
    return new Distance(km * 1000);
  }

  static fromYards(yards: number): Distance {
    return new Distance(yards * 0.9144);
  }

  get meters(): number {
    return this._meters;
  }

  get kilometers(): number {
    return this._meters / 1000;
  }

  get yards(): number {
    return this._meters / 0.9144;
  }

  get miles(): number {
    return this._meters / 1609.34;
  }

  equals(other: Distance): boolean {
    return this._meters === other._meters;
  }

  add(other: Distance): Distance {
    return new Distance(this._meters + other._meters);
  }

  subtract(other: Distance): Distance {
    const result = this._meters - other._meters;
    if (result < 0) {
      throw new Error('Result cannot be negative');
    }
    return new Distance(result);
  }

  multiply(factor: number): Distance {
    if (factor < 0) {
      throw new Error('Factor cannot be negative');
    }
    return new Distance(this._meters * factor);
  }

  toString(unit: 'meters' | 'kilometers' | 'yards' | 'miles' = 'meters'): string {
    switch (unit) {
      case 'meters':
        return `${this._meters.toFixed(0)}m`;
      case 'kilometers':
        return `${this.kilometers.toFixed(2)}km`;
      case 'yards':
        return `${this.yards.toFixed(0)}yd`;
      case 'miles':
        return `${this.miles.toFixed(2)}mi`;
      default:
        return `${this._meters.toFixed(0)}m`;
    }
  }
}
